import * as React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Printer } from 'lucide-react';
import { api } from '@/lib/api';
import { imprimirRomaneio } from '@/lib/imprimirPapel';
import { Botao } from '@/componentes/ui/botao';
import { Esqueleto } from '@/componentes/ui/primitivos';
import {
  CSS_ROMANEIO,
  RomaneioPapel,
  type LinhaRomaneio,
} from '@/componentes/romaneio/RomaneioPapel';

/**
 * O romaneio A4 aberto no EDGE, para imprimir com prévia e escolha de
 * impressora.
 *
 * O Chrome do checkout roda com `--kiosk-printing`: é o que faz a etiqueta do
 * crachá sair sozinha, e é também o que o impede de mostrar o diálogo de
 * impressão — o A4 ia direto para a Zebra. O botão "Imprimir Romaneio" da tela
 * de Etiquetas abre ESTA página no Edge (`microsoft-edge:`), que não tem esse
 * modo: o diálogo aparece, com a prévia e a lista de impressoras. O Chrome e a
 * etiqueta não mudam.
 *
 * Fica fora do login: o Edge não tem a sessão do WMS. Quem autoriza é o token
 * `t` da URL, curto e preso a este romaneio (ver `link-a4` na API). Também fica
 * fora do AppShell — e portanto da impressão automática de etiquetas, que não
 * pode rodar numa segunda janela.
 */
interface RomaneioDetalhe {
  cabecalho: {
    romaneio: number;
    data: string | null;
    transportadoraNome: string | null;
  };
  linhas: LinhaRomaneio[];
  qtdePedidos: number;
  totalVolumes: number;
  totalPesoGramas: number;
}

export function RomaneioA4(): JSX.Element {
  const { romaneio } = useParams();
  const [busca] = useSearchParams();
  const token = busca.get('t') ?? '';
  const papel = React.useRef<HTMLDivElement>(null);
  const jaAbriu = React.useRef(false);

  const consulta = useQuery<RomaneioDetalhe>({
    queryKey: ['romaneio-a4', romaneio, token],
    queryFn: () => api.get<RomaneioDetalhe>(`/api/romaneios/${romaneio}/a4`, { t: token }),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const imprimir = React.useCallback((): void => {
    if (papel.current && consulta.data) imprimirRomaneio(papel.current, consulta.data.cabecalho.romaneio);
  }, [consulta.data]);

  // Abre o diálogo sozinho uma vez: quem clicou em "Imprimir Romaneio" quer imprimir.
  React.useEffect(() => {
    if (!consulta.data || jaAbriu.current) return;
    jaAbriu.current = true;
    imprimir();
  }, [consulta.data, imprimir]);

  return (
    <div className="min-h-screen bg-superficie2 p-4">
      <div className="mx-auto max-w-[230mm] space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="rotulo">Romaneio de Expedição</p>
            <h1 className="tabular font-mono text-[22px] font-bold leading-tight">{romaneio}</h1>
          </div>
          <Botao variante="primaria" onClick={imprimir} disabled={!consulta.data}>
            <Printer className="h-4 w-4" />
            Imprimir
          </Botao>
        </div>

        {consulta.isLoading ? (
          <Esqueleto className="h-[297mm] w-full" />
        ) : consulta.error ? (
          <p className="flex items-start gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {consulta.error.message}
          </p>
        ) : consulta.data ? (
          <>
            <style>{CSS_ROMANEIO}</style>
            <div className="cartao overflow-x-auto bg-white p-4">
              <div ref={papel} style={{ width: '210mm', maxWidth: '100%' }}>
                <RomaneioPapel
                  romaneio={consulta.data.cabecalho.romaneio}
                  data={consulta.data.cabecalho.data}
                  transportadora={consulta.data.cabecalho.transportadoraNome}
                  linhas={consulta.data.linhas}
                  qtdePedidos={consulta.data.qtdePedidos}
                  totalVolumes={consulta.data.totalVolumes}
                  totalPesoGramas={consulta.data.totalPesoGramas}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
